import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { ResponsiveLine } from '@nivo/line';

import { formatDate } from '../../../lib/helpers/date.helpers';
import { milestoneDisplaySegments } from '../../../lib/helpers/player-milestone.helpers';
import { PaletteColor, PLAYER_MILESTONE_TYPE_LABELS, PLAYER_MILESTONE_TYPE_UNITS } from '../../../lib/types';

interface IMilestoneEvolutionChartProps {
  type: PlayerMilestoneType;
  entries: IPlayerMilestone[];
}

const LINE_COLOR = PaletteColor.GREEN.hex;

export const MilestoneEvolutionChart: React.FC<IMilestoneEvolutionChartProps> = ({ type, entries }) => {
  if (!entries.length) {
    return <Typography color="textSecondary">No measurements recorded yet.</Typography>;
  }

  const label = PLAYER_MILESTONE_TYPE_LABELS[type];
  const unit = PLAYER_MILESTONE_TYPE_UNITS[type];
  const yAxisLabel = `${label} (${unit})`;
  const data = [{
    id: label,
    color: LINE_COLOR,
    data: entries.map((entry) => ({ x: formatDate(entry.recordedAt), y: entry.value })),
  }];

  return (
    <Box height={320} width="100%">
      <ResponsiveLine
        data={data}
        margin={{ top: 20, right: 30, bottom: 70, left: 60 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
        axisBottom={{ tickRotation: -45, legend: 'Date', legendOffset: 56, legendPosition: 'middle' }}
        axisLeft={{ legend: yAxisLabel, legendOffset: -50, legendPosition: 'middle' }}
        colors={[LINE_COLOR]}
        pointSize={8}
        pointColor="#fff"
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        useMesh
        animate
        tooltip={({ point }) => {
          // Nivo anchors the tooltip above the point; flip it below when the
          // point sits near the top so it doesn't overflow the chart area.
          const nearTop = point.y < 90;
          return (
            <Box
              style={{
                background: 'white',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: 4,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transform: nearTop ? 'translateY(calc(100% + 20px))' : undefined,
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 700 }}>Date: {point.data.xFormatted}</Typography>
              <Typography variant="body2">
                {label}:{' '}
                {milestoneDisplaySegments(type, Number(point.data.y)).map((segment, index) => (
                  <span key={index} style={segment.unit ? { fontWeight: 700 } : undefined}>{segment.text}</span>
                ))}
              </Typography>
            </Box>
          );
        }}
      />
    </Box>
  );
};
